/* global HTMLElement */
import Swiper from 'swiper';

/**
 * Registers multiple block styles
 *
 * @param {string} namespace Button block namespace
 * @param {Array}  styles    Styles array containing name and label
 *
 * @return {void}
 */
export function registerBlockStyles( namespace, styles ) {
	styles.forEach( style => {
		wp.blocks.registerBlockStyle( namespace, style );
	} );
}

/**
 * Creates an HTML Element from a css- selector.
 * Used for filling in blanks in structures that are not prepared for swiper.
 * (ie missing next/prev buttons or container for bullets)
 *
 * @see https://stackoverflow.com/a/71964580
 * @param {HTMLElement}        parentNode
 * @param {string|HTMLElement} selector
 * @return {HTMLElement} New element.
 */
function getOrCreate( parentNode, selector ) {
	if ( selector instanceof HTMLElement ) {
		return selector;
	}

	if ( parentNode.querySelector( selector ) ) {
		return parentNode.querySelector( selector );
	}

	const pattern = /^(.*?)(?:#(.*?))?(?:\.(.*?))?(?:@(.*?)(?:=(.*?))?)?$/;
	const matches = selector.match( pattern );
	const element = document.createElement( matches[ 1 ] || 'div' );

	if ( matches[ 2 ] ) {
		element.id = matches[ 2 ];
	}

	if ( matches[ 3 ] ) {
		element.className = matches[ 3 ];
	}

	if ( matches[ 4 ] ) {
		element.setAttribute( matches[ 4 ], matches[ 5 ] || '' );
	}

	parentNode.appendChild( element );

	return element;
}

/**
 * Adds requirements to make a list of elements into a slider
 *
 * @param {string} selector         - selector for the wrapper you want to turn into a slider
 * @param {Object} [sliderSettings] - settings to be passed to the slider
 * @return {Swiper[]} - returns an array of newly created Swiper objects
 */
export function addSlider( selector, sliderSettings = {} ) {
	return [ ... document.querySelectorAll( selector ) ].map( function( target ) {
		// Allow for completely disabling some sliders.
		const disableSlider = wp.hooks.applyFilters( 'qala_theme.filter.pre_disable_slider', false, target, sliderSettings );
		if ( disableSlider ) {
			return '';
		}

		let thisSliderSettings = { navigation: {}, pagination: {} };

		if ( sliderSettings?.navigation?.closest ) {
			const outerWrapper = target.closest( sliderSettings.navigation.closest );

			thisSliderSettings.navigation.nextEl = outerWrapper.querySelector( sliderSettings.navigation.nextEl );
			thisSliderSettings.navigation.prevEl = outerWrapper.querySelector( sliderSettings.navigation.prevEl );
		}

		if ( sliderSettings?.pagination?.el && ! ( sliderSettings.pagination.el instanceof HTMLElement ) ) {
			thisSliderSettings.pagination.el = getOrCreate( target, sliderSettings.pagination.el );
		}

		const swiperWrapper = target.children[ 0 ];
		if ( ! swiperWrapper.classList.contains( 'swiper-wrapper' ) ) {
			swiperWrapper.classList.add( 'swiper-wrapper' );
		}

		const swiperSlides = swiperWrapper.children;
		for ( const swiperSlide of swiperSlides ) {
			if ( ! swiperSlide.classList.contains( 'swiper-slide' ) ) {
				swiperSlide.classList.add( 'swiper-slide' );
			}
		}

		if ( target.className === 'popular-products-slider' || target.className === 'products-slider' || target.className === 'wc-block-product-best-sellers' ) {
			const numberColumns = parseInt( target.getAttribute( 'data-columns' ) );
			const slides = target.querySelectorAll( 'ul > li' ); // covers Qala and Woo css.
			const numberSlides = slides.length;

			// if number of slides is equal or less than columns, disable loop mode to avoid console warning.
			if ( numberSlides <= numberColumns ) {
				thisSliderSettings.loop = false;
			}
		}

		thisSliderSettings = wp.hooks.applyFilters( 'qala_theme.filter.slider_settings', { ...sliderSettings, ...thisSliderSettings }, target );
		return new Swiper( target, thisSliderSettings );
	} );
}
